/**
 * apps/battle/client/src/terminal/screens/VerifyGateScreen.tsx
 *
 * Phase 4.2 M12b-ui1 — client-side verify GATE screen.
 *
 * Under M12b the server returns 403 {error:'verify_email_required'} on every
 * content route for an authenticated-but-unverified student. This screen is
 * shown (via TerminalApp's `needsVerify` branch) INSTEAD of the infinite
 * "> loading map…" dead-end. Verification is REQUIRED to play — it is NOT
 * optional. The student can resend the link, re-check (re-fetch the map) once
 * they've clicked it, or log out.
 */
import { useState } from 'react';
import { api, ApiError } from '../../api.js';
import { TerminalFrame } from '../components/TerminalFrame.js';
import { KeyBar, type KeyBarItem } from '../components/KeyBar.js';
import { Spinner } from '../../widgets.js';

interface VerifyGateScreenProps {
  email: string;
  /**
   * Re-fetch the map. Resolves true if the map loaded (verified → into game),
   * false if it still 403s (still unverified).
   */
  onRecheck: () => Promise<boolean>;
  onLogout: () => void;
}

export function VerifyGateScreen({ email, onRecheck, onLogout }: VerifyGateScreenProps) {
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resendErr, setResendErr] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [stillUnverified, setStillUnverified] = useState(false);

  async function resend() {
    setResendStatus('sending');
    setResendErr(null);
    try {
      await api.post('/api/auth/request-verify', {});
      setResendStatus('sent');
    } catch (err) {
      setResendStatus('error');
      if (err instanceof ApiError && err.status === 429) {
        setResendErr('Please wait a few minutes before requesting another email.');
      } else {
        setResendErr('Could not resend verification email. Please try again later.');
      }
    }
  }

  async function recheck() {
    setChecking(true);
    setStillUnverified(false);
    try {
      const ok = await onRecheck();
      // On success TerminalApp clears needsVerify and renders the game; this
      // component unmounts. If it still 403s we stay and show the notice.
      if (!ok) setStillUnverified(true);
    } finally {
      setChecking(false);
    }
  }

  const keybar: KeyBarItem[] = [
    { label: 'Enter', description: "I've verified — continue", onActivate: () => { void recheck(); }, disabled: checking },
    { label: 'R', description: 'resend verification', onActivate: () => { void resend(); }, disabled: resendStatus === 'sending' },
    { label: 'L', description: 'log out', onActivate: onLogout },
  ];

  return (
    <TerminalFrame
      title={<>ECONQUEST &nbsp;&nbsp;─&nbsp;&nbsp;VERIFY YOUR EMAIL</>}
      keybar={<KeyBar items={keybar} />}
    >
      <div style={{ maxWidth: 520, margin: '0 auto', paddingTop: 24, textAlign: 'left' }}>
        <h2 style={{ color: '#e6ebf2', fontSize: 18, marginTop: 0 }}>Verify your email to start playing</h2>
        <p style={{ color: '#cdd5e0', lineHeight: 1.6 }}>
          You must verify your email to start playing EconQuest. We've sent a
          verification link to <strong style={{ color: '#e6ebf2' }}>{email}</strong>.
          Click the link in that email, then come back and choose “I've verified — continue”.
        </p>
        <p style={{ color: '#97a3b6', lineHeight: 1.6, fontSize: 13 }}>
          Verification is required for gameplay — you won't be able to enter the
          map until your email is confirmed.
        </p>

        {stillUnverified && (
          <div style={{ marginTop: 12, padding: 12, background: '#2a230f', border: '1px solid #6d5a1f', borderRadius: 6, color: '#e8d7a7', fontSize: 13 }}>
            Still not verified — check your inbox (and spam folder) for the verification link, then try again.
          </div>
        )}
        {resendStatus === 'sent' && (
          <div style={{ marginTop: 12, padding: 12, background: '#0f2a1c', border: '1px solid #1f6d3e', borderRadius: 6, color: '#a7e8c5', fontSize: 13 }}>
            Verification email resent.
          </div>
        )}
        {resendStatus === 'error' && resendErr && (
          <div style={{ marginTop: 12, padding: 12, background: '#2a0f0f', border: '1px solid #6d1f1f', borderRadius: 6, color: '#e8a7a7', fontSize: 13 }}>
            {resendErr}
          </div>
        )}

        <div style={{ marginTop: 24, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" type="button" onClick={() => { void recheck(); }} disabled={checking}>
            {checking ? <Spinner size={14} /> : "I've verified — continue"}
          </button>
          <button className="btn" type="button" onClick={() => { void resend(); }} disabled={resendStatus === 'sending'}>
            {resendStatus === 'sending' ? <Spinner size={14} /> : 'Resend email'}
          </button>
          <button className="btn" type="button" onClick={onLogout}>
            Log out
          </button>
        </div>
      </div>
    </TerminalFrame>
  );
}
